import {
  mayFinalizeMaintenanceWorkOrder,
  type MaintenanceWorkOrderPriority,
} from '@gb-ticket/shared-types';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { usePageTitle } from '@/hooks/use-page-title';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { ApiRequestError } from '@/lib/api';
import { MaintenanceRequiredSelect } from '@/products/maintenance/features/maintenance-filter-select';
import { MaintenanceFormPage } from '@/products/maintenance/features/maintenance-form-page';
import { MaintenanceInstallationSearchSelect } from '@/products/maintenance/features/maintenance-installation-search-select';
import { newMaintenanceWorkOrderCreateIdempotencyKey } from '@/products/maintenance/features/work-orders/maintenance-work-order-idempotency';
import {
  useCreateMaintenanceWorkOrder,
  useMaintenanceTechnicians,
  useMaintenanceWorkOrder,
  useUpdateMaintenanceWorkOrder,
} from '@/products/maintenance/features/work-orders/use-maintenance-work-orders';
import { usePublishedTemplateVersions } from '@/products/maintenance/features/work-orders/use-published-template-versions';
import {
  MAINTENANCE_PLANNING_PATH,
  MAINTENANCE_TEMPLATES_PATH,
  maintenanceWorkOrderPath,
} from '@/products/maintenance/maintenance-paths';

const PRIORITY_OPTIONS: { value: MaintenanceWorkOrderPriority; label: string }[] = [
  { value: 'low', label: 'Niedrig' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'Hoch' },
];

export function MaintenanceWorkOrderFormPage() {
  const copy = useVoiceCopy();
  const { workOrderId = '' } = useParams();
  const isEdit = Boolean(workOrderId);
  usePageTitle(isEdit ? 'Auftrag bearbeiten' : 'Auftrag anlegen');
  const navigate = useNavigate();
  const existing = useMaintenanceWorkOrder(isEdit ? workOrderId : '');
  const create = useCreateMaintenanceWorkOrder();
  const update = useUpdateMaintenanceWorkOrder(workOrderId);
  const technicians = useMaintenanceTechnicians();
  const [installationId, setInstallationId] = useState('');
  const versions = usePublishedTemplateVersions(isEdit ? '' : installationId);
  const [templateVersionId, setTemplateVersionId] = useState('');
  const [assignedToId, setAssignedToId] = useState('');
  const [scheduledFor, setScheduledFor] = useState('');
  const [dueOn, setDueOn] = useState('');
  const [priority, setPriority] = useState<MaintenanceWorkOrderPriority>('normal');
  const [instructions, setInstructions] = useState('');
  const [requireTechnicianSignature, setRequireTechnicianSignature] = useState(true);
  const [requireCustomerSignature, setRequireCustomerSignature] = useState(false);
  const [confirmReassign, setConfirmReassign] = useState(false);
  const [syncedId, setSyncedId] = useState('');
  const [createIdempotencyKey] = useState(() => newMaintenanceWorkOrderCreateIdempotencyKey());

  const row = existing.data;
  if (isEdit && row && syncedId !== row.id) {
    setSyncedId(row.id);
    setAssignedToId(row.assignedToId);
    setScheduledFor(row.scheduledFor ?? '');
    setDueOn(row.dueAt ? row.dueAt.slice(0, 10) : '');
    setPriority(row.priority);
    setInstructions(row.instructions ?? '');
    setRequireTechnicianSignature(row.requireTechnicianSignature);
    setRequireCustomerSignature(row.requireCustomerSignature);
  }

  const signatureFlagsLocked = isEdit && row != null && mayFinalizeMaintenanceWorkOrder(row.status);

  const needsReassignConfirm =
    row != null &&
    mayFinalizeMaintenanceWorkOrder(row.status) &&
    assignedToId !== '' &&
    assignedToId !== row.assignedToId;

  async function onCreate() {
    if (!installationId || !templateVersionId || !assignedToId) {
      toast.error('Bitte Anlage, Vorlagenversion und Monteur wählen.');
      return;
    }
    try {
      const created = await create.mutateAsync({
        installationId,
        templateVersionId,
        assignedToId,
        scheduledFor: scheduledFor || undefined,
        status: 'assigned',
        priority,
        instructions: instructions || null,
        requireTechnicianSignature,
        requireCustomerSignature,
        dueAt: dueOn ? `${dueOn}T00:00:00.000Z` : undefined,
        idempotencyKey: createIdempotencyKey,
      });
      toast.success('Auftrag angelegt.');
      void navigate(maintenanceWorkOrderPath(created.id));
    } catch (err) {
      const message =
        err instanceof ApiRequestError ? err.message : 'Der Auftrag konnte nicht angelegt werden.';
      toast.error(message);
    }
  }

  async function onUpdate() {
    if (!row || !assignedToId) {
      toast.error('Bitte einen Monteur wählen.');
      return;
    }
    try {
      const saved = await update.mutateAsync({
        revision: row.revision,
        assignedToId,
        scheduledFor: scheduledFor || null,
        dueAt: dueOn ? `${dueOn}T00:00:00.000Z` : null,
        priority,
        instructions: instructions || null,
        ...(signatureFlagsLocked ? {} : { requireTechnicianSignature, requireCustomerSignature }),
        ...(needsReassignConfirm ? { confirmInProgressReassignment: confirmReassign } : {}),
      });
      toast.success('Auftrag gespeichert.');
      void navigate(maintenanceWorkOrderPath(saved.id));
    } catch (err) {
      const message =
        err instanceof ApiRequestError
          ? err.message
          : 'Der Auftrag konnte nicht gespeichert werden.';
      toast.error(message);
    }
  }

  if (isEdit && existing.isPending) {
    return <p className="text-sm text-muted-foreground">Auftrag wird geladen …</p>;
  }
  if (isEdit && (existing.isError || !row)) {
    return <p className="text-sm text-destructive">Der Auftrag wurde nicht gefunden.</p>;
  }
  if (isEdit && row && (row.status === 'completed' || row.status === 'cancelled')) {
    return (
      <p className="text-sm text-muted-foreground">
        Abgeschlossene oder stornierte Aufträge können nicht geändert werden.{' '}
        <Link className="text-primary hover:underline" to={maintenanceWorkOrderPath(row.id)}>
          Zurück zum Auftrag
        </Link>
      </p>
    );
  }

  return (
    <MaintenanceFormPage
      title={isEdit ? 'Auftrag bearbeiten' : 'Auftrag anlegen'}
      description={
        isEdit
          ? 'Termin, Monteur, Priorität, Hinweise und Fälligkeit. Die Vorlagenversion bleibt.'
          : 'Wartungsauftrag planen und einem Monteur zuweisen.'
      }
      cancelHref={isEdit && row ? maintenanceWorkOrderPath(row.id) : MAINTENANCE_PLANNING_PATH}
      pending={create.isPending || update.isPending}
      onSubmit={() => {
        void (isEdit ? onUpdate() : onCreate());
      }}
    >
      <div className="space-y-4">
        <h2 className="text-base font-semibold">Auftrag</h2>
        {isEdit && row ? (
          <p className="text-sm text-muted-foreground">
            {row.orderNumber} · {row.installation.installationNumber} – {row.installation.name} ·{' '}
            {row.templateName} v{row.templateVersionNumber}
          </p>
        ) : (
          <>
            <MaintenanceInstallationSearchSelect
              value={installationId}
              onChange={(id) => {
                setInstallationId(id);
                setTemplateVersionId('');
              }}
            />
            <MaintenanceRequiredSelect
              label="Vorlagenversion"
              id="wo-template"
              value={templateVersionId}
              disabled={!installationId || versions.isPending}
              onValueChange={(value) => {
                setTemplateVersionId(value);
                const version = versions.data?.items.find((item) => item.id === value);
                setRequireCustomerSignature(version?.requireCustomerSignature ?? false);
              }}
              items={
                versions.data?.items.map((v) => ({
                  value: v.id,
                  label: `${v.templateName} v${v.versionNumber}`,
                })) ?? []
              }
            />
            {installationId && !versions.isPending && (versions.data?.items.length ?? 0) === 0 ? (
              <p className="text-sm text-muted-foreground">
                {copy('maintenance.template.missing.before')}{' '}
                <Link className="text-primary hover:underline" to={MAINTENANCE_TEMPLATES_PATH}>
                  Vorlagen
                </Link>{' '}
                {copy('maintenance.template.missing.after')}
              </p>
            ) : null}
            {versions.isError ? (
              <p className="text-sm text-destructive">
                Vorlagenversionen konnten nicht geladen werden.
              </p>
            ) : null}
          </>
        )}
        <MaintenanceRequiredSelect
          label="Monteur"
          id="wo-tech"
          value={assignedToId}
          onValueChange={setAssignedToId}
          items={
            technicians.data?.items.map((t) => ({
              value: t.id,
              label: t.displayName,
            })) ?? []
          }
        />
        {needsReassignConfirm ? (
          <label className="flex min-h-11 items-center gap-3 text-sm">
            <input
              type="checkbox"
              className="size-4"
              checked={confirmReassign}
              onChange={(event) => setConfirmReassign(event.target.checked)}
            />
            Neuzuweisung während der Bearbeitung bestätigen
          </label>
        ) : null}
        <MaintenanceRequiredSelect
          label="Priorität"
          id="wo-priority"
          value={priority}
          onValueChange={(value) => setPriority(value as MaintenanceWorkOrderPriority)}
          items={PRIORITY_OPTIONS}
        />
        <div className="space-y-2">
          <Label htmlFor="wo-date">Termin</Label>
          <Input
            id="wo-date"
            type="date"
            className="min-h-11"
            value={scheduledFor}
            onChange={(e) => setScheduledFor(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="wo-due">Fälligkeit</Label>
          <Input
            id="wo-due"
            type="date"
            className="min-h-11"
            value={dueOn}
            onChange={(e) => setDueOn(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <p className="text-sm font-medium">Unterschriften</p>
          <label className="flex min-h-11 items-center gap-3 text-sm">
            <input
              type="checkbox"
              className="size-4"
              checked={requireTechnicianSignature}
              disabled={signatureFlagsLocked}
              onChange={(event) => setRequireTechnicianSignature(event.target.checked)}
            />
            Monteur-Unterschrift erforderlich
          </label>
          <label className="flex min-h-11 items-center gap-3 text-sm">
            <input
              type="checkbox"
              className="size-4"
              checked={requireCustomerSignature}
              disabled={signatureFlagsLocked}
              onChange={(event) => setRequireCustomerSignature(event.target.checked)}
            />
            Unterschrift vor Ort erforderlich (Kunde, Beauftragter oder Ansprechpartner)
          </label>
          {signatureFlagsLocked ? (
            <p className="text-sm text-muted-foreground">
              Nach dem Start der Wartung bleiben die Unterschriftspflichten fest.
            </p>
          ) : null}
        </div>
        <div className="space-y-2">
          <Label htmlFor="wo-notes">Hinweise</Label>
          <textarea
            id="wo-notes"
            className="flex min-h-24 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
            value={instructions}
            onChange={(event) => setInstructions(event.target.value)}
          />
        </div>
      </div>
    </MaintenanceFormPage>
  );
}
